import ApiController from './ApiController';
import Questionario, { QuestionarioStatus } from '../models/Questionario';
import QuestionarioSecao from '../models/QuestionarioSecao';
import QuestionarioQuestao, { TIPO_ESCOLHA, TIPO_INTEIRO, TIPO_MULTIPLAESCOLHA, TIPO_NUMERICO, TIPO_TEXTO_FIXO } from '../models/QuestionarioQuestao';
import QuestionarioOpcaoResposta from '../models/QuestionarioOpcaoResposta';
import ErroQuestionario from '../models/ErroQuestionario';
import ErroQuestionarioSecao from '../models/ErroQuestionarioSecao';
import ErroQuestionarioQuestao from '../models/ErroQuestionarioQuestao';
import ErroQuestionarioOpcao from '../models/ErroQuestionarioOpcao';
import { converterNumero, validarDecimal } from '../utils/validacao';

/**
 * Retorno do método
 * @typedef Retorno
 * @property {Boolean} sucesso Indica se o método foi executado com êxito
 * @property {ErroQuestionario} erros Mensagem de erro ou objeto com as validações de cada campo
 * @property {Questionario} questionario Dados do questionário salvo
 */

/**
 * Retorno do método listarSelecao
 * @typedef RetornoListarSelecao
 * @property {Boolean} sucesso Indica se o método foi executado com êxito
 * @property {Array<Questionario>} items Questionários retornados pela API
 */

/**
 * Controller para incluir, editar, listar e obter dados de questionários
 */
class QuestionarioController {

    constructor() {
        this.apiController = new ApiController();
    }

    /**
     * Adiciona um novo questionário na API
     * @param {Questionario} questionario Dados do questionário a ser salvo
     * @returns {Promise<Retorno>} Retorna um objeto com a propriedade sucesso e a lista de erros ou o questionário salvo
     */
    async salvar(questionario) {
        //Valida os dados do questionário
        const erros = this._validarQuestionario(questionario);

        //Retorna os erros para a aplicação
        if (!erros.valido)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = this._extrairCampos(questionario, true);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.post('/questionario', body);
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            const erros = error.response ? error.response.data : [{ mensagem: error.message || 'Erro de conexão com o servidor' }];
            return { sucesso: false, erros };
        }
    }

    /**
     * 
     * @param {Questionario} questionario 
     * @return {Promise<Retorno>}
     */
    async atualizar(questionario) {
        const erros = await this._validarQuestionario(questionario);

        //Retorna os erros para a aplicação
        if (!erros.valido)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = await this._extrairCampos(questionario, false);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.put('/questionario/' + questionario.id.toString(), body);
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Recupera os dados de um questionário na API
     * @param {Number} id Id do questionário a ser recuperado
     * @returns {Promise<Retorno>} Retorna os dados do questionário consultado
     */
    async obter(id) {
        //Verifica se o ID está preenchido
        if (!id)
            return { sucesso: false, erros: [{ campo: 'id', mensagem: 'É necessário' }] };

        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/questionario/' + id.toString());
            //Retorna indicando que houve sucesso e os dados do questionário
            return { sucesso: true, questionario: Questionario.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Lista os questionários cadastrados, paginando e filtrando de acordo com os parâmetros
     * @param {Number} page Número da página da listagem
     * @param {Number} pageSize Quantidade de registros que devem ser retornados
     * @param {Object} params Filtros adicionais para a listagem
     * @param {String} params.titulo Filtro do título do questionário
     * @param {String} params.status Filtro do status do questionário
     */
    async listar(page, pageSize, { titulo, status }) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/questionario', { page, pageSize, titulo, status });
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            //Mapeia os itens para objetos do tipo Questionario
            const items = result.items.map(item => Questionario.clone(item));
            result.items = items;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Lista os questionários, podendo ser filtrado por empresa
     * @param {Number} empresa Identificação da empresa a ser filtrada (opcional)
     * @returns {Promise<RetornoListarSelecao>} Retorna a lista de questionários, sem dados de paginação
     */
    async listarSelecao(empresa) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: items } = await this.apiController.get('/questionario', { pagination: false, empresa });
            //Marca que houve retorno com sucesso
            const result = {
                sucesso: true,
                items: items.map(item => Questionario.clone(item))
            };
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Obtém os dados de um questionário e cria uma cópia para ser inserido novamente
     * @param {Number} id Id do questionário a ser duplicado
     * @returns {Promise<Retorno>} Retorna um questionário com os mesmos dados do anterior
     */
    async duplicar(id) {
        //Verifica se o ID está preenchido
        if (!id)
            return { sucesso: false, erros: [{ campo: 'id', mensagem: 'É necessário' }] };

        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: questionario } = await this.apiController.get('/questionario/' + id.toString());
            const result = Questionario.clone(questionario);

            //Concatena a informação de que o questionário está sendo copiado
            result.titulo += ' - Cópia';
            //Remove os ids dos itens
            result.id = null;
            result.secoes.forEach(secao => {
                secao.id = null;
                secao.questoes.forEach(questao => {
                    questao.id = null;
                    if (questao.tipo === TIPO_ESCOLHA || questao.tipo === TIPO_MULTIPLAESCOLHA)
                        questao.opcoes.forEach(opcao => {
                            opcao.id = null;
                            //Copia a imagem para um campo para permitir a cópia da mesma
                            opcao.copiaImagem = opcao.imagem;
                        });
                });
            });
            //Retorna indicando que houve sucesso e os dados do questionário
            return { sucesso: true, questionario: result };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Valida os campos de um questionário
     * @param {Questionario} questionario Questionário que será validado
     * @returns {ErroQuestionario} Retorna a lista de erros encontrados
     */
    _validarQuestionario(questionario) {
        let erros = new ErroQuestionario();

        //Valida se o objeto foi informado
        if (!questionario) {
            erros.erroQuestionario = 'Questionário é necessário';
            erros.valido = false;
        }
        else {
            //Valida se o questionário possui um título
            if (!questionario.titulo) {
                erros.titulo = 'É necessário';
                erros.valido = false;
            }

            //Validando se o campo modelo está preenchido
            if (typeof questionario.modelo === 'undefined' || questionario.modelo === null) {
                erros.modelo = 'É necessário';
                erros.valido = false;
            }

            //Validando se o campo exibeCliente está preenchido
            if (typeof questionario.exibeCliente === 'undefined' || questionario.exibeCliente === null) {
                erros.exibeCliente = 'É necessário';
                erros.valido = false;
            }
            //Caso não seja um modelo, valida a empresa e o status
            if (!questionario.modelo) {
                //Caso o campo empresa esteja presente, valida o valor
                if (questionario.empresa && isNaN(questionario.empresa)) {
                    erros.empresa = 'Precisa ser numérico';
                    erros.valido = false;
                }

                //Valida se o status está preenchido
                if (!questionario.status) {
                    erros.status = 'É necessário';
                    erros.valido = false;
                    //Valida se o valor do status é um dos valores esperado
                } else if (!QuestionarioStatus.includes(questionario.status)) {
                    erros.status = 'Não é um status válido';
                    erros.valido = false;
                }
            }

            //Verifica se as seções foram informadas
            let erroSecoes = this._validarArray(questionario.secoes, 'secoes');
            if (erroSecoes) {
                erros.erroSecoes = erroSecoes;
                erros.valido = false;
            }
            else {
                //Valida as seções individualmente
                for (let i = 0; i < questionario.secoes.length; i++) {
                    //Valida a seção
                    let errosSecao = this._validarSecao(questionario.secoes[i]);
                    //Adiciona os erros na lista
                    erros.secoes.push(errosSecao);
                    //Caso a seção possua erros, todo o objeto é marcado como inválido
                    if (!errosSecao.valido)
                        erros.valido = false;
                }
            }
        }

        return erros;
    }

    /**
     * Valida os dados de uma seção
     * @param {QuestionarioSecao} secao Dados da seção a ser validada
     * @returns {ErroQuestionarioSecao} Retorna a lista de erros encontrados
     */
    _validarSecao(secao) {
        let erros = new ErroQuestionarioSecao();

        //Valida se a seção foi informada
        if (!secao) {
            erros.erroSecao = 'Seção é necessária';
            erros.valido = false;
        }
        else {
            //Valida se a seção possui um título
            if (!secao.titulo) {
                erros.titulo = 'É necessário';
                erros.valido = false;
            }

            //Verifica se as questões foram informadas
            let erroQuestoes = this._validarArray(secao.questoes);
            if (erroQuestoes) {
                erros.erroQuestoes = erroQuestoes;
                erros.valido = false;
            }
            else {
                //Valida as questões individualmente
                for (let i = 0; i < secao.questoes.length; i++) {
                    //Valida a questão
                    let errosQuestao = this._validarQuestao(secao.questoes[i]);
                    //Adiciona os erros na lista
                    erros.questoes.push(errosQuestao);
                    //Caso a questão possua erros, todo o objeto é marcado como inválido
                    if (!errosQuestao.valido)
                        erros.valido = false;
                }
            }

        }

        return erros;
    }

    /**
     * Valida os dados de uma questão
     * @param {QuestionarioQuestao} questao Questão a ser validada
     * @returns {ErroQuestionarioQuestao} Retorna a lista de erros encontrados
     */
    _validarQuestao(questao) {
        let erros = new ErroQuestionarioQuestao();

        //Verifica se a questão foi informada
        if (!questao) {
            erros.erroQuestao = 'Questão é necessária';
            erros.valido = false;
        }
        else {
            //Verifica se a questão possui um título
            if (!questao.titulo) {
                erros.titulo = 'É necessário';
                erros.valido = false;
            }

            //Verifica se o campo 'obrigatoria' foi informado
            if (questao.obrigatoria === undefined) {
                erros.obrigatoria = 'É necessário';
                erros.valido = false;
            }

            //Verifica se a questão possui um tipo
            if (!questao.tipo) {
                erros.tipo = 'É necessário';
                erros.valido = false;
            }
            //Verifica as informações específicas para os tipos 'escolha' e 'multiplaEscolha'
            else {
                if (questao.tipo === TIPO_INTEIRO || questao.tipo === TIPO_NUMERICO || questao.tipo === TIPO_ESCOLHA) {
                    if (questao.exibeGrafico === undefined) {
                        erros.exibeGrafico = 'É necessário';
                        erros.valido = false;
                    }
                }

                if (questao.tipo === TIPO_INTEIRO || questao.tipo === TIPO_NUMERICO) {
                    if (questao.limitaValor === undefined) {
                        erros.limitaValor = 'É necessário';
                        erros.valido = false;
                    } else if (questao.limitaValor) {
                        if (questao.valorMinimo === undefined) {
                            erros.valorMinimo = 'É necessário';
                            erros.valido = false;
                        }
                        if (questao.valorMaximo === undefined) {
                            erros.valorMaximo = 'É necessário';
                            erros.valido = false;
                        }
                    }
                }

                if (questao.tipo === TIPO_ESCOLHA || questao.tipo === TIPO_MULTIPLAESCOLHA) {
                    //Valida se o campo 'opcaoOutros' foi informado
                    if (questao.opcaoOutros === undefined) {
                        erros.opcaoOutros = 'É necessário';
                        erros.valido = false;
                    }

                    //Valida se as opções de resposta foram informadas
                    let erroOpcoes = this._validarArray(questao.opcoes);
                    if (erroOpcoes) {
                        erros.erroOpcoes = erroOpcoes;
                        erros.valido = false;
                    }
                    else {
                        //Valida as opções de resposta individualmente
                        for (let i = 0; i < questao.opcoes.length; i++) {
                            //Valida a opção de resposta
                            let errosOpcao;
                            if (questao.exibeGrafico)
                                errosOpcao = this._validarValorOpcaoResposta(questao.opcoes[i]);
                            else
                                errosOpcao = this._validarOpcaoResposta(questao.opcoes[i]);
                            //Adicina na lista
                            erros.opcoes.push(errosOpcao);
                            //Caso a questão possua erros, todo o objeto é marcado como inválido
                            if (!errosOpcao.valido)
                                erros.valido = false;
                        }
                    }
                }

                if (questao.tipo === TIPO_TEXTO_FIXO) {
                    //Verifica se a questão possui um texto
                    if (!questao.conteudoTexto) {
                        erros.conteudoTexto = 'É necessário';
                        erros.valido = false;
                    }

                    //Verifica se o campo 'exibeResultado' foi informado
                    if (questao.exibeResultado === undefined) {
                        erros.exibeResultado = 'É necessário';
                        erros.valido = false;
                    }
                }
            }

        }

        return erros;
    }

    /**
     * Valida os dados de uma opção de resposta
     * @param {QuestionarioOpcaoResposta} opcao Dados da opção de resposta a ser validada
     * @returns {ErroQuestionarioOpcao} Lista com os erros encontrados
     */
    _validarOpcaoResposta(opcao) {
        let erros = new ErroQuestionarioOpcao();

        //Verifica se a opção de resposta foi informada
        if (!opcao) {
            erros.erroOpcao = 'Opção de resposta é necessária';
            erros.valido = false;
        } else {
            //Verifica se a reposta possui ao menos uma descrição ou uma imagem (nova ou já incluída)
            if (!opcao.descricao && !opcao.novaImagem && !opcao.imagem && !opcao.copiaImagem) {
                erros.descricao = 'É necessário';
                erros.imagem = 'É necessário';
                erros.valido = false;
            }
        }

        return erros;
    }

    /**
     * Valida os dados de uma opção de resposta numérica
     * @param {QuestionarioOpcaoResposta} opcao Dados da opção de resposta a ser validada
     * @returns {ErroQuestionarioOpcao} Lista com os erros encontrados
     */
    _validarValorOpcaoResposta(opcao) {
        let erros = new ErroQuestionarioOpcao();

        //Verifica se a opção de resposta foi informada
        if (!opcao) {
            erros.erroOpcao = 'Opção de resposta é necessária';
            erros.valido = false;
        } else {
            //Verifica se a reposta possui ao menos uma descrição ou uma imagem (nova ou já incluída)
            if (opcao.valor === undefined) {
                erros.valor = 'É necessário';
                erros.valido = false;
            } else if (!validarDecimal(opcao.valor) || isNaN(converterNumero(opcao.valor))) {
                erros.valor = 'Precisa ser um valor numérico';
                erros.valido = false;
            }
        }

        return erros;
    }

    /**
     * Valida um array, verificando se é do tipo esperado
     * @param {Array} array Lista a ser validada
     * @returns {String} Retorna a descrição do erro encontrado
     */
    _validarArray(array) {
        let erro = '';

        //Valida se o array foi informado
        if (!array)
            erro = 'É necessário';
        //Valida se é realmente um array
        else if (!Array.isArray(array))
            erro = 'Precisa ser uma lista';
        //Valida se possui ao menos um item
        else if (array.length === 0)
            erro = 'Precisa ter ao menos um item';

        return erro;
    }

    /**
     * Extrai apenas os dados do questionário que serão enviados na requisição
     * @param {Questionario} questionario Objeto original contendo os dados
     * @param {Boolean} inclusao Indica se a requisição é uma inclusão ou alteração
     * @returns {Questionario} Retorna uma cópia do original apenas com os dados necessários
     */
    _extrairCampos(questionario, inclusao) {
        const result = new Questionario();
        result.titulo = questionario.titulo;
        result.secoes = questionario.secoes.map(secao => this._extrairCamposSecao(secao, inclusao));
        result.modelo = questionario.modelo;
        result.exibeCliente = questionario.exibeCliente;
        if (questionario.empresa) {
            result.empresa = questionario.empresa;
        } else {
            result.empresa = null;
        }

        if (questionario.modelo) {
            result.status = null;
        } else {
            result.status = questionario.status;
        }

        return result;
    }

    /**
     * Extrai os dados da seção
     * @param {QuestionarioSecao} secao Seção original contendo os dados
     * @param {Boolean} inclusao Indica se a requisição é uma inclusão ou alteração
     * @returns {QuestionarioSecao} Retorna uma cópia do original contendo apenas os dados necessários
     */
    _extrairCamposSecao(secao, inclusao) {
        const result = new QuestionarioSecao();
        result.titulo = secao.titulo;
        result.questoes = secao.questoes.map(questao => this._extrairCamposQuestao(questao, inclusao));

        if (!inclusao && secao.id) {
            result.id = secao.id;
        }

        return result;
    }

    /**
     * Extrai os dados da questão
     * @param {QuestionarioQuestao} questao Questão original contendo os dados
     * @param {Boolean} inclusao Indica se a requisição é uma inclusão ou alteração
     * @returns {QuestionarioQuestao} Retorna uma cópia do original contendo apenas os dados necessários
     */
    _extrairCamposQuestao(questao, inclusao) {
        const result = new QuestionarioQuestao();
        result.titulo = questao.titulo;
        result.obrigatoria = questao.obrigatoria;
        result.tipo = questao.tipo;
        result.opcaoOutros = null;
        result.opcoes = null;
        result.exibeGrafico = null;

        if (!inclusao && questao.id) {
            result.id = questao.id;
        }

        if (questao.tipo === TIPO_ESCOLHA || questao.tipo === TIPO_MULTIPLAESCOLHA) {
            result.opcaoOutros = questao.opcaoOutros;
        }

        if (questao.tipo === TIPO_INTEIRO || questao.tipo === TIPO_NUMERICO || questao.tipo === TIPO_ESCOLHA) {
            result.exibeGrafico = questao.exibeGrafico;
        }

        if (questao.tipo === TIPO_INTEIRO || questao.tipo === TIPO_NUMERICO) {
            result.limitaValor = questao.limitaValor;
            if (questao.limitaValor) {
                result.valorMinimo = converterNumero(questao.valorMinimo);
                result.valorMaximo = converterNumero(questao.valorMaximo);
            }
        }

        if (questao.tipo === TIPO_ESCOLHA && questao.exibeGrafico)
            result.opcoes = questao.opcoes.map(opcao => this._extrairValorOpcaoResposta(opcao, inclusao));
        else if (questao.tipo === TIPO_ESCOLHA || questao.tipo === TIPO_MULTIPLAESCOLHA)
            result.opcoes = questao.opcoes.map(opcao => this._extrairCamposOpcaoResposta(opcao, inclusao));

        if (questao.tipo === TIPO_TEXTO_FIXO) {
            result.conteudoTexto = questao.conteudoTexto;
            result.exibeResultado = questao.exibeResultado;
        }

        return result;
    }

    /**
     * Extrai os dados da opção de resposta
     * @param {QuestionarioOpcaoResposta} opcao Opção de resposta original contendo os dados
     * @param {Boolean} inclusao Indica se a requisição é uma inclusão ou alteração
     * @returns {QuestionarioOpcaoResposta} Retorna uma cópia do original contedo apenas os dados necessário
     */
    _extrairCamposOpcaoResposta(opcao, inclusao) {
        const result = new QuestionarioOpcaoResposta();

        if (opcao.descricao) {
            result.descricao = opcao.descricao;
        } else {
            result.descricao = null;
        }

        if (opcao.novaImagem) {
            result.imagem = opcao.novaImagem;
        } else if (inclusao && opcao.copiaImagem) { //Caso seja inclusão e o campo novaImagem estiver preenchido
            result.imagem = null;
            result.copiaImagem = opcao.copiaImagem;
        } else {
            result.imagem = null;
        }


        if (!inclusao && opcao.id) {
            result.id = opcao.id;
        }

        return result;
    }

    /**
     * Extrai o valor das opções de resposta numéricas
     * @param {QuestionarioOpcaoResposta} opcao Opção de resposta original contendo os dados
     * @param {Boolean} inclusao Indica se a requisição é uma inclusão ou alteração
     * @returns {QuestionarioOpcaoResposta} Retorna uma cópia do original contedo apenas os dados necessário
     */
    _extrairValorOpcaoResposta(opcao, inclusao) {
        const result = new QuestionarioOpcaoResposta();
        result.descricao = null;
        result.imagem = null;
        result.novaImagem = null;
        result.copiaImagem = null;

        result.valor = converterNumero(opcao.valor);
        if (isNaN(result.valor))
            result.valor = null;

        if (!inclusao && opcao.id) {
            result.id = opcao.id;
        }

        return result;
    }
}

export default QuestionarioController;